import * as styles from "./AbsenceDialog.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type RefObject, useEffect } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  addAbsenceAtom,
  errorAtom,
  hideAbsenceManagerAtom,
  isDirtyAtom,
  isVisibleAtom,
  loadAbsencesAtom,
  loadBalanceAtom,
  loadingAtom,
  saveAtom,
  savingAtom,
} from "../stores";
import { AbsenceDetail } from "./AbsenceDetail";
import { AbsenceIdentity } from "./AbsenceIdentity";
import { AbsenceList } from "./AbsenceList";

export type AbsenceDialogProps = {
  onSaved?: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function AbsenceDialog({ onSaved, originRef }: AbsenceDialogProps) {
  const { t } = useLingui();
  const isVisible = useAtomValue(isVisibleAtom);
  const loading = useAtomValue(loadingAtom);
  const saving = useAtomValue(savingAtom);
  const error = useAtomValue(errorAtom);
  const dirty = useAtomValue(isDirtyAtom);

  const hide = useSetAtom(hideAbsenceManagerAtom);
  const loadAbsences = useSetAtom(loadAbsencesAtom);
  const loadBalance = useSetAtom(loadBalanceAtom);
  const addAbsence = useSetAtom(addAbsenceAtom);
  const save = useSetAtom(saveAtom);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    void loadAbsences();
    void loadBalance();
  }, [isVisible, loadAbsences, loadBalance]);

  const handleSave = async () => {
    if (await save()) {
      onSaved?.();
    }
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          hide();
        }
      }}
      originRef={originRef}
    >
      <DialogContent size="xl">
        <DialogHeader>
          <AbsenceIdentity />

          <div className={styles.headerActions}>
            <Button
              variant="primary"
              size="sm"
              leftSection={<Icon name="Plus" size="sm" />}
              label={t`Add`}
              onClick={() => void addAbsence()}
            />

            <DialogClose />
          </div>
        </DialogHeader>

        {error && <p className={styles.error}>{error}</p>}

        {/* The panes are rendered right away and fill themselves in: the
            layout is known before the data is, so waiting on a single loader
            would only delay the frame the user can already read. */}
        <div className={styles.layout} aria-busy={loading}>
          <div className={styles.listPane}>
            <AbsenceList />
          </div>

          <div className={styles.detailPane}>
            <AbsenceDetail />
          </div>
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={() => hide()}>
            <Trans>Close</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={!dirty}
            onClick={() => void handleSave()}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
